import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';

const ClaimRequest = () => {
  // 1. URL 쿼리 스트링(?order_id=318&item_id=511)을 읽기 위한 설정
  const [searchParams] = useSearchParams();
  const order_id = searchParams.get('order_id'); 
  const order_item_id = searchParams.get('item_id'); // URL의 item_id 키값과 매칭
  
  const navigate = useNavigate();

  const [item, setItem] = useState(null);
  const [type, setType] = useState('exchange');
  const [reason, setReason] = useState('');
  const [image, setImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // 2. 파라미터가 유효한지 먼저 체크
    if (!order_id || !order_item_id) {
      alert("잘못된 접근입니다. 주문번호와 상품번호가 없습니다.");
      navigate(-1);
      return;
    }

    const fetchItemInfo = async () => {
      try {
        // 백엔드 API 호출 (/api/claim_request/318/511 형태)
        const res = await axios.get(`/api/claim_request/${order_id}/${order_item_id}`);
        setItem(res.data.item);
      } catch (err) {
        console.error("Fetch Error:", err);
        alert("상품 정보를 불러올 수 없습니다.");
        navigate(-1);
      } finally {
        setLoading(false);
      }
    };

    fetchItemInfo();
  }, [order_id, order_item_id, navigate]);

  // 이미지 선택 시 미리보기 처리
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      const reader = new FileReader();
      reader.onloadend = () => setPreviewUrl(reader.result);
      reader.readAsDataURL(file);
    }
  };

  // 이미지 리사이징 함수 (Canvas 사용)
  const resizeImage = (file, maxWidth) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          let width = img.width;
          let height = img.height;
          if (width > maxWidth) {
            const scale = maxWidth / width;
            width = maxWidth;
            height *= scale;
          }
          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);
          canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.9);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  // 폼 제출 처리
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!reason) return alert("신청 사유를 입력하세요.");

    const formData = new FormData();
    formData.append('type', type);
    formData.append('reason', reason);

    if (image) {
      const resizedBlob = await resizeImage(image, 1200);
      const timestamp = Date.now();
      formData.append('image', resizedBlob, `claim_${timestamp}_${image.name}`);
    }

    try {
      const res = await axios.post(`/api/claim_request/${order_id}/${order_item_id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (res.data.success) {
        alert(res.data.message);
        navigate('/claim'); // 성공 시 내역 페이지로 이동
      }
    } catch (err) {
      alert("업로드 실패: " + (err.response?.data?.message || "서버 오류"));
    }
  };

  if (loading) return <div className="container mt-5 text-center">데이터 로딩 중...</div>;

  return (
    <div className="container" style={styles.container}>
      <h3 className="mb-4">교환/반품 신청</h3>
      {item && (
        <div style={styles.itemBox}>
          <p className="fw-bold">대상 상품: {item.name}</p>
          <p className="text-muted" style={{ fontSize: '0.9rem' }}>주문번호: {order_id}</p>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-floating mb-3">
          <select 
            className="form-select" 
            id="type"
            value={type} 
            onChange={(e) => setType(e.target.value)}
          >
            <option value="exchange">교환</option>
            <option value="return">반품</option>
          </select>
          <label htmlFor="type">신청 유형</label>
        </div>

        <div className="form-floating mb-3">
          <textarea 
            className="form-control" 
            placeholder="신청 사유를 입력하세요" 
            id="reason"
            style={{ height: '150px' }}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required
          ></textarea>
          <label htmlFor="reason">신청 사유</label>
        </div>

        <div className="mb-4">
          <label className="form-label fw-bold">사진 첨부 (선택)</label>
          <input 
            className="form-control" 
            type="file" 
            accept="image/*" 
            onChange={handleFileChange}
          />
          {previewUrl && (
            <div style={styles.previewContainer}>
              <img src={previewUrl} alt="미리보기" style={styles.previewImg} />
            </div>
          )}
        </div>

        <div className="text-end">
          <button type="submit" className="btn btn-primary me-2" style={styles.submitBtn}>신청하기</button>
          <button type="button" className="btn btn-secondary" onClick={() => navigate(-1)}>취소</button>
        </div>
      </form>
    </div>
  );
};

// 스타일 정의
const styles = {
  container: { maxWidth: '800px', margin: '0px auto 50px', padding: '20px' },
  itemBox: { background: '#f8f9fa', padding: '15px', borderRadius: '8px', marginBottom: '20px', borderLeft: '5px solid #00704a' },
  previewContainer: { marginTop: '15px', textAlign: 'center', background: '#eee', borderRadius: '8px', padding: '10px' },
  previewImg: { maxWidth: '100%', maxHeight: '400px', borderRadius: '5px' },
  submitBtn: { padding: '8px 25px', fontWeight: 'bold' }
};

export default ClaimRequest;